/**
 * CharacterPreview Custom Element (headless)
 *
 * A non-visual API bridge that turns character inputs into a generated
 * preview image via the preview API. It renders nothing: consumers own all
 * display (buttons, spinners, image elements, error messaging) and drive it
 * through the public API below.
 *
 * Public API:
 *   - data: { illustrationId, replacements[], turnstileContainer } (get/set)
 *     Each replacement accepts { characterId, faceUrl, eyeColor, age, gender }.
 *     The legacy top-level single-character fields remain supported.
 *   - illustrationId: string                                     (get/set)
 *   - characterId: string                                        (get/set)
 *   - state: "idle" | "loading" | "success" | "error"            (readonly)
 *   - lastRunId: string   render job id once create succeeds     (readonly)
 *   - onImage: (imageUrl: string) => void                        (callback)
 *   - requestPhoto(): Promise<string>  resolves the generated image URL
 *
 * Events (bubbling, composed):
 *   - "character-preview:state"  detail: { state, previousState }
 *   - "character-preview:queued" detail: { runId }  — fired as soon as the render job exists
 *   - "character-preview:image"  detail: { imageUrl, runId }
 *   - "character-preview:error"  detail: { error, reason }
 *   - "character-preview:progress" detail: { progress, eta }
 */

const CHARACTER_PREVIEW_DEFAULTS = {
  baseUrl: "https://book-api-production-298d.up.railway.app/",
  pollIntervalMs: 2000,
};

const CHARACTER_PREVIEW_TERMINAL_STATUSES = new Set([
  "completed",
  "partial",
  "failed",
]);

function previewApiUrl(baseUrl, path) {
  return new URL(path, baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`);
}

async function previewApiFetch(baseUrl, path, init = {}) {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const fetcher = init.method === "POST" ? window.ChildbookStorefrontSession.fetch : fetch;
  const response = await fetcher(previewApiUrl(baseUrl, path), {
    ...init,
    headers,
  });
  const text = await response.text();
  let data = null;

  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    throw new Error(
      `Invalid preview API response (${response.status}): ${text.slice(0, 500)}`,
    );
  }

  if (!response.ok) {
    const message =
      data && typeof data.error === "string"
        ? `${data.error}${data.errorCode ? ` (${data.errorCode})` : ""}`
        : text || response.statusText;
    throw new Error(
      `Preview API request failed: ${response.status} ${message}`,
    );
  }

  return data;
}

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildReferenceImageUrl(faceUrl) {
  if (/^https?:\/\//i.test(faceUrl)) {
    return faceUrl;
  }

  const s3Key = faceUrl.startsWith("s3://")
    ? faceUrl
    : `s3://childbook-photo-uploads/${faceUrl.replace(/^\/+/, "")}`;

  return `https://images.childbook.ai/sig/${base64EncodeS3Key(s3Key)}`;
}

window.ChildbookPhoto = {
  resolveUrl: buildReferenceImageUrl,
};

function base64EncodeS3Key(s3Key) {
  const parts = s3Key.split("/");
  const fileName = parts.pop();
  const safeKey = fileName
    ? `${parts.join("/")}/${encodeURIComponent(fileName)}`.replaceAll(
        "%20",
        " ",
      )
    : s3Key;

  return btoa(safeKey);
}

function buildFacePrompt({ age, eyeColor, gender, facePrompt }) {
  if (typeof facePrompt === "string" && facePrompt.trim()) {
    return facePrompt.trim();
  }

  const subject =
    gender === "male" ? "a boy" : gender === "female" ? "a girl" : "a child";
  const ageText = Number.isFinite(age) ? `${age} years old` : "";
  const eyeText = eyeColor ? `with ${eyeColor} eyes` : "";

  return [subject, ageText, eyeText].filter(Boolean).join(" ");
}

function buildClientRequestId() {
  if (window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `character-preview-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

/**
 * Creates an illustration-template-render job, polls until it reaches a terminal
 * status, then returns the first generated image URL along with the run id that
 * produced it. `onQueued` fires as soon as the create response returns a runId,
 * so callers can offer "notify me when ready" before the image exists.
 */
async function fetchPreviewImage(data, { signal, onProgress, onQueued } = {}) {
  const baseUrl = data.baseUrl || CHARACTER_PREVIEW_DEFAULTS.baseUrl;
  const replacements =
    Array.isArray(data.replacements) && data.replacements.length > 0
      ? data.replacements
      : [data];
  const createBody = {
    illustrationId: data.illustrationId,
    replacements: replacements.map((replacement) => ({
      characterId: replacement.characterId,
      referenceImageUrl: buildReferenceImageUrl(replacement.faceUrl),
      facePrompt: buildFacePrompt(replacement),
    })),
    clientRequestId: data.clientRequestId || buildClientRequestId(),
    shopifyDomain: "ndab0u-g5.myshopify.com",
  };

  if (!window.childbookStorefront?.enabled && !data.skipCaptcha) {
    createBody.captchaToken = await ensureToken(data);
  }

  if (Number.isFinite(data.seed)) {
    createBody.seed = data.seed;
  }

  const created = await previewApiFetch(
    baseUrl,
    "api/illustration-template-render/",
    {
      method: "POST",
      body: JSON.stringify(createBody),
      signal,
    },
  );

  if (!created?.runId) {
    throw new Error("Preview API create response did not contain a runId");
  }

  if (typeof onQueued === "function") {
    try {
      onQueued({ runId: created.runId });
    } catch {
      /* ignore queued callback errors */
    }
  }

  const pollIntervalMs =
    data.pollIntervalMs || CHARACTER_PREVIEW_DEFAULTS.pollIntervalMs;
  let status = null;

  do {
    await sleep(pollIntervalMs);
    if (signal?.aborted) {
      throw new DOMException("Preview request was aborted", "AbortError");
    }

    status = await previewApiFetch(
      baseUrl,
      `api/illustration-template-render/${created.runId}`,
      { method: "GET", signal },
    );

    if (typeof onProgress === "function" && status) {
      try {
        onProgress({ progress: status.progress, eta: status.eta });
      } catch {
        /* ignore progress callback errors */
      }
    }
  } while (!CHARACTER_PREVIEW_TERMINAL_STATUSES.has(status?.status));

  const results = await previewApiFetch(
    baseUrl,
    `api/illustration-template-render/${created.runId}/results`,
    { method: "GET", signal },
  );

  if (results?.status === "failed") {
    throw new Error(status?.errorMessage || "Preview generation failed");
  }

  const imageUrl = results?.resultImages?.find(
    (url) => typeof url === "string" && url,
  );
  if (!imageUrl) {
    throw new Error("Preview API results did not contain an image URL");
  }

  return { imageUrl, runId: created.runId };
}

async function ensureToken(data) {
  const presetToken =
    typeof data.turnstileToken === "string" && data.turnstileToken.trim()
      ? data.turnstileToken.trim()
      : typeof data.captchaToken === "string" && data.captchaToken.trim()
        ? data.captchaToken.trim()
        : "";

  if (presetToken) return presetToken;

  const getToken = window.theme?.turnstile?.getToken;
  if (typeof getToken !== "function") {
    throw new Error("Security check is unavailable. Please reload the page.");
  }

  const token = await getToken({
    container: data.turnstileContainer,
    action: "create_preview",
  });

  if (typeof token !== "string" || !token.trim()) {
    throw new Error("Security check did not return a token. Please try again.");
  }

  return token.trim();
}

if (!customElements.get("character-preview")) {
  customElements.define(
    "character-preview",
    class CharacterPreview extends HTMLElement {
      constructor() {
        super();
        this._data = this.normalizeData();
        this._state = "idle";
        this._requestToken = 0;
        this._onImage = null;
        this._lastRunId = "";
      }

      // ----- data -----
      get data() {
        return {
          ...this._data,
          replacements: this._data.replacements.map((replacement) => ({
            ...replacement,
          })),
        };
      }

      set data(value) {
        this._data = this.normalizeData(value);
        // Invalidate any in-flight request: inputs changed, so a pending
        // response is now stale and must not call onImage.
        this._requestToken++;
        this._lastRunId = "";
      }

      get illustrationId() {
        return this._data.illustrationId;
      }

      set illustrationId(value) {
        this.setDataField("illustrationId", value);
      }

      get characterId() {
        return this._data.characterId;
      }

      set characterId(value) {
        this.setDataField("characterId", value);
      }

      // ----- state (readonly) -----
      get state() {
        return this._state;
      }

      // Render job id once the create call succeeds (before the image is ready).
      // Consumers that need to reference the render server-side (for example to
      // queue a notify-when-ready email) must pass this id rather than an image URL.
      get lastRunId() {
        return this._lastRunId;
      }

      setState(next) {
        if (next === this._state) return;
        const previousState = this._state;
        this._state = next;
        this.dispatchEvent(
          new CustomEvent("character-preview:state", {
            bubbles: true,
            composed: true,
            detail: { state: next, previousState },
          }),
        );
      }

      // ----- onImage callback -----
      get onImage() {
        return this._onImage;
      }

      set onImage(fn) {
        this._onImage = typeof fn === "function" ? fn : null;
      }

      // ----- public request -----
      /**
       * Requests a preview image for the current data.
       * @returns {Promise<string>} resolves with the generated image URL.
       */
      async requestPhoto() {
        const data = this._data;

        const missing = this.getMissingFields(data);
        if (missing.length > 0) {
          const error = new Error(
            `Missing required preview data: ${missing.join(", ")}`,
          );
          this.setState("error");
          this.dispatchEvent(
            new CustomEvent("character-preview:error", {
              bubbles: true,
              composed: true,
              detail: { error, reason: "missing-data", missing },
            }),
          );
          throw error;
        }

        const token = ++this._requestToken;
        this.setState("loading");

        try {
          const self = this;
          const { imageUrl, runId } = await fetchPreviewImage(
            { ...data },
            {
              onQueued({ runId: queuedRunId }) {
                if (token !== self._requestToken) return;
                self._lastRunId = queuedRunId || "";
                self.dispatchEvent(
                  new CustomEvent("character-preview:queued", {
                    bubbles: true,
                    composed: true,
                    detail: { runId: self._lastRunId },
                  }),
                );
              },
              onProgress(info) {
                self.dispatchEvent(
                  new CustomEvent("character-preview:progress", {
                    bubbles: true,
                    composed: true,
                    detail: info,
                  }),
                );
              },
            },
          );

          // Drop stale responses: data changed (token bumped) while in flight.
          if (token !== this._requestToken) {
            return imageUrl;
          }

          this._lastRunId = runId || this._lastRunId || "";
          this.setState("success");

          if (this._onImage) {
            try {
              this._onImage(imageUrl);
            } catch (callbackError) {
              console.error(
                "character-preview onImage callback error:",
                callbackError,
              );
            }
          }

          this.dispatchEvent(
            new CustomEvent("character-preview:image", {
              bubbles: true,
              composed: true,
              detail: { imageUrl, runId: this._lastRunId },
            }),
          );

          return imageUrl;
        } catch (error) {
          // Ignore failures from a request that has since been superseded.
          if (token !== this._requestToken) {
            throw error;
          }

          this.setState("error");
          this.dispatchEvent(
            new CustomEvent("character-preview:error", {
              bubbles: true,
              composed: true,
              detail: { error, reason: "request-failed" },
            }),
          );
          throw error;
        }
      }

      // ----- helpers -----
      normalizeData(value = {}) {
        const next = value || {};
        return {
          baseUrl:
            this.getStringValue(next.baseUrl, "data-api-base-url") ||
            CHARACTER_PREVIEW_DEFAULTS.baseUrl,
          illustrationId: this.getStringValue(
            next.illustrationId,
            "data-illustration-id",
          ),
          characterId: this.getStringValue(
            next.characterId,
            "data-character-id",
          ),
          faceUrl: typeof next.faceUrl === "string" ? next.faceUrl.trim() : "",
          eyeColor:
            typeof next.eyeColor === "string" ? next.eyeColor.trim() : "",
          age: this.parseAge(next.age),
          gender: typeof next.gender === "string" ? next.gender.trim() : "",
          facePrompt:
            typeof next.facePrompt === "string" ? next.facePrompt.trim() : "",
          replacements: Array.isArray(next.replacements)
            ? next.replacements.slice(0, 3).map((replacement) =>
                this.normalizeReplacement(replacement),
              )
            : [],
          clientRequestId:
            typeof next.clientRequestId === "string"
              ? next.clientRequestId.trim()
              : "",
          turnstileContainer:
            next.turnstileContainer instanceof HTMLElement
              ? next.turnstileContainer
              : null,
          turnstileToken:
            typeof next.turnstileToken === "string"
              ? next.turnstileToken.trim()
              : "",
          skipCaptcha: next.skipCaptcha === true,
          captchaToken:
            typeof next.captchaToken === "string"
              ? next.captchaToken.trim()
              : "",
          seed: this.parseOptionalNumber(next.seed),
          pollIntervalMs:
            this.parseOptionalNumber(next.pollIntervalMs) ||
            this.parseOptionalNumber(
              this.getAttribute("data-poll-interval-ms"),
            ) ||
            CHARACTER_PREVIEW_DEFAULTS.pollIntervalMs,
        };
      }

      setDataField(fieldName, value) {
        this._data = this.normalizeData({
          ...this._data,
          [fieldName]: typeof value === "string" ? value.trim() : "",
        });
        this._requestToken++;
        this._lastRunId = "";
      }

      getStringValue(value, attributeName) {
        if (typeof value === "string" && value.trim()) {
          return value.trim();
        }

        const attributeValue = this.getAttribute(attributeName);
        return attributeValue?.trim() || "";
      }

      parseAge(value) {
        if (value === null || value === undefined || value === "") return null;
        const num = typeof value === "number" ? value : Number(value);
        return Number.isFinite(num) ? num : null;
      }

      parseOptionalNumber(value) {
        if (value === null || value === undefined || value === "")
          return undefined;
        const num = typeof value === "number" ? value : Number(value);
        return Number.isFinite(num) ? num : undefined;
      }

      normalizeReplacement(value) {
        const replacement = value || {};
        return {
          characterId:
            typeof replacement.characterId === "string"
              ? replacement.characterId.trim()
              : "",
          faceUrl:
            typeof replacement.faceUrl === "string"
              ? replacement.faceUrl.trim()
              : "",
          eyeColor:
            typeof replacement.eyeColor === "string"
              ? replacement.eyeColor.trim()
              : "",
          age: this.parseAge(replacement.age),
          gender:
            typeof replacement.gender === "string"
              ? replacement.gender.trim()
              : "",
          facePrompt:
            typeof replacement.facePrompt === "string"
              ? replacement.facePrompt.trim()
              : "",
        };
      }

      getMissingFields(data) {
        const missing = [];
        if (!data.illustrationId || typeof data.illustrationId !== "string")
          missing.push("illustrationId");
        const replacements = data.replacements.length
          ? data.replacements
          : [data];
        if (replacements.length > 3) missing.push("replacements");
        replacements.forEach((replacement, index) => {
          const prefix = replacements.length > 1 ? `replacements[${index}].` : "";
          if (!replacement.faceUrl || typeof replacement.faceUrl !== "string")
            missing.push(`${prefix}faceUrl`);
          if (!replacement.eyeColor || typeof replacement.eyeColor !== "string")
            missing.push(`${prefix}eyeColor`);
          if (!Number.isFinite(replacement.age)) missing.push(`${prefix}age`);
          if (
            !replacement.characterId ||
            typeof replacement.characterId !== "string"
          )
            missing.push(`${prefix}characterId`);
        });
        return missing;
      }
    },
  );
}
